import { createPinch, type Find } from "./pinch.ts";

export const reducedMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

const bezier = [0.2, 0.8, 0.2, 1] as const;
const ease = `cubic-bezier(${bezier.join(", ")})`;
const length = 280;
const outRate = length / 220;

type Part = readonly [element: Element, frames: Keyframe[], easing?: string];

export function visible(element: Element, room = 0): boolean {
  const box = element.getBoundingClientRect();
  return box.bottom > 0 && box.top < innerHeight - room && box.width > 0;
}

export function windows(stage: HTMLElement, front: HTMLElement, back: HTMLElement): void {
  const box = stage.getBoundingClientRect();
  const under = document.documentElement.scrollHeight - scrollY - box.bottom;
  stage.style.cssText = `min-height: ${box.height}px; --topo: ${box.top + scrollY}px; --fundo: ${under}px; --largura: ${box.width}px`;
  front.dataset["janela"] = "frente";
  back.dataset["janela"] = "fundo";
}

export function flow(stage: HTMLElement, level: HTMLElement): void {
  const scroll = level.scrollTop;
  delete level.dataset["janela"];
  stage.style.cssText = "";
  scrollTo(0, scroll);
}

function timeOf(progress: number): number {
  if (progress <= 0 || progress >= 1) return Math.min(1, Math.max(0, progress));
  const [x1, y1, x2, y2] = bezier;
  const at = (a: number, b: number, t: number) => 3 * a * t * (1 - t) ** 2 + 3 * b * t ** 2 * (1 - t) + t ** 3;
  let [low, high] = [0, 1];
  for (let step = 0; step < 24; step += 1) {
    const middle = (low + high) / 2;
    if (at(y1, y2, middle) < progress) low = middle;
    else high = middle;
  }
  return at(x1, x2, (low + high) / 2);
}

const timing = { duration: length, fill: "both" } as const;
function hold([element, frames, easing = "linear"]: Part): Animation {
  const animation = element.animate(frames, { ...timing, easing });
  animation.pause();
  return animation;
}

function late(deep: Element, clock: Animation, from: number): Animation[] {
  const header = deep.querySelector("[data-morph]");
  if (!header || !visible(header)) return [];
  const blocks: Element[] = [];
  for (let at = header; at !== deep && at.parentElement; at = at.parentElement) {
    for (const block of at.parentElement.children) if (block !== at) blocks.push(block);
  }
  return blocks.map((block) => {
    const animation = hold([block, [{ opacity: 0 }, { opacity: 0, offset: from, easing: ease }, { opacity: 1 }]]);
    animation.currentTime = clock.currentTime;
    animation.playbackRate = clock.playbackRate;
    if (clock.playState === "running") animation.play();
    return animation;
  });
}

export function shoot(deep: HTMLElement, shallow: HTMLElement, origin: Element | null) {
  const card = origin && visible(origin) ? origin.getBoundingClientRect() : null;
  const column = deep.firstElementChild?.getBoundingClientRect();
  const scale = card && column ? Math.min(1, Math.max(0.12, card.width / column.width)) : 0.985;
  const [x, y] = card && column ? [card.left - column.left * scale, card.top - Math.max(0, column.top) * scale] : [0, 0];
  const covers = scale > 0.8 ? 0.1 : 0.3;
  const grow = hold([deep, [{ transform: `translate(${x}px, ${y}px) scale(${scale})` }, { transform: "none" }], ease]);
  const parts: Part[] = [
    [deep, [{ opacity: 0 }, { opacity: 1, offset: covers }, { opacity: 1 }]],
    [shallow, [{ transform: "none", opacity: 1 }, { transform: "scale(0.965)", opacity: 0.3 }], ease],
  ];
  if (card && origin) parts.push([origin, [{ opacity: 1 }, { opacity: 0, offset: covers }, { opacity: 0 }]]);
  const camera = [grow, ...parts.map(hold)];
  let header = deep.querySelector("[data-morph]");
  let blocks = late(deep, grow, covers + 0.05);
  const page = new MutationObserver(() => {
    const now = deep.querySelector("[data-morph]");
    if (now === header) return;
    header = now;
    for (const block of blocks) block.cancel();
    blocks = late(deep, grow, covers + 0.05);
  });
  page.observe(deep, { childList: true, subtree: true });
  return {
    seek(progress: number) {
      for (const animation of [...camera, ...blocks]) animation.currentTime = timeOf(progress) * length;
    },
    play(to: 0 | 1) {
      for (const animation of [...camera, ...blocks]) {
        animation.playbackRate = to === 1 ? 1 : -outRate;
        if (animation.currentTime === to * length) animation.finish();
        else animation.play();
      }
      return Promise.allSettled(camera.map((animation) => animation.finished));
    },
    cut() {
      page.disconnect();
      for (const animation of [...camera, ...blocks]) animation.cancel();
    },
  };
}

export function arrive(level: Element, side: "prev" | "next" | undefined): void {
  const slides = side !== undefined && !reducedMotion();
  const from = slides ? { opacity: 0, left: side === "prev" ? "-16px" : "16px" } : { opacity: 0 };
  const timing = slides ? { duration: 160, easing: ease } : { duration: reducedMotion() ? 90 : 120 };
  level.animate([from, { opacity: 1, left: 0 }], timing);
}

export function watchPinch(find: Find<Element>): () => void {
  const pinch = createPinch(find, reducedMotion, (run, ms) => {
    const timer = setTimeout(run, ms);
    return () => clearTimeout(timer);
  });
  const onStage = (target: EventTarget | null) => (target instanceof Element && target.closest("[data-palco]") ? target : null);
  const pair = (touches: TouchList) => {
    const [a, b] = [touches[0], touches[1]];
    if (!a || !b || touches.length !== 2) return null;
    const [dx, dy] = [a.clientX - b.clientX, a.clientY - b.clientY];
    return { apart: Math.hypot(dx, dy), x: b.clientX + dx / 2, y: b.clientY + dy / 2 };
  };
  let fingers: { readonly apart: number; readonly over: Element } | null = null;

  const wheel = (event: WheelEvent) => {
    const target = event.ctrlKey ? onStage(event.target) : null;
    if (target && pinch.wheel(event.deltaY, target)) event.preventDefault();
  };
  const counted = (event: TouchEvent) => {
    pinch.lift();
    const two = pair(event.touches);
    const over = two && onStage(document.elementFromPoint(two.x, two.y));
    fingers = two && over ? { apart: two.apart, over } : null;
  };
  const moved = (event: TouchEvent) => {
    const two = fingers && pair(event.touches);
    if (fingers && two && pinch.touch(two.apart / fingers.apart, fingers.over) && event.cancelable) event.preventDefault();
  };
  const counts = ["touchstart", "touchend", "touchcancel"] as const;
  document.addEventListener("wheel", wheel, { passive: false });
  document.addEventListener("touchmove", moved, { passive: false });
  for (const type of counts) document.addEventListener(type, counted);
  return () => {
    pinch.lift();
    document.removeEventListener("wheel", wheel);
    document.removeEventListener("touchmove", moved);
    for (const type of counts) document.removeEventListener(type, counted);
  };
}
